import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHomeChat } from '../../../context';
import { useDeleteDocument } from '../../../hooks/useDeleteDocument';
import { useSaveDocumentToDrive } from '../../../hooks/useSaveDocumentToDrive/useSaveDocumentToDrive';
import { generatePdf, generateDocx } from '../../../../../../components/DocumentCanvas/DocumentUtils';
import { useToast } from '../../../../../../components/toast';

const ADD_FROM_DRIVE_VALUES = ['drive', 'addFromDrive'];
const SAVE_TO_DRIVE_VALUES = ['save_to_drive', 'saveToDrive', 'drive'];
const GO_TO_DRIVE_VALUES = ['go_to_drive', 'goToDrive', 'goto_drive'];
const DOWNLOAD_PDF_VALUES = ['pdf', 'download_pdf', 'downloadPdf', 'download'];
const DOWNLOAD_DOCX_VALUES = ['docx', 'download_docx', 'downloadDocx'];

/**
 * Encapsulates all document-related actions for ChatSlideOut:
 * - Mapping sortedDocuments to display format
 * - Opening the DocumentPanel when a document is selected
 * - Download (PDF/DOCX), Save to Drive, Delete from the conversation document card
 * - State for SaveToDriveModal and DeleteDocumentModal
 *
 * Returns commonDocProps (ready to spread into ConversationView/AgentView/EmptyStateView)
 * and modal state + handlers to render modals in the parent component.
 */
export const useSlideOutDocuments = ({ onAddFromDriveRequest } = {}) => {
  const {
    sortedDocuments,
    openDocumentPanel,
    getAgentById,
    getDocument,
    updateDocument,
    selectedAgentId,
    userId,
    tenantId,
  } = useHomeChat();

  const toast = useToast();
  const navigate = useNavigate();
  const { handleDeleteDocument } = useDeleteDocument();

  const [saveToDriveDocId, setSaveToDriveDocId] = useState(null);
  const [showSaveToDriveModal, setShowSaveToDriveModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const docToSave = saveToDriveDocId ? getDocument(saveToDriveDocId) : null;

  const { saveToVault, isSaving } = useSaveDocumentToDrive({
    getContent: () => {
      const doc = saveToDriveDocId ? getDocument(saveToDriveDocId) : null;
      const content = doc?.content ?? '';
      return typeof content === 'string' ? content : { toText: () => String(content ?? '') };
    },
    getTitle: () => {
      const doc = saveToDriveDocId ? getDocument(saveToDriveDocId) : null;
      return doc?.title ?? 'Document';
    },
    getAgentInfo: () => ({ agentUuid: selectedAgentId }),
    getAuthContext: () => ({ userId, tenantId }),
    onSuccess: (message) => toast.success({ message }),
    onError: (error) => toast.error({ message: error?.message ?? 'Failed to save document.' }),
  });

  const mappedDocuments = useMemo(
    () =>
      sortedDocuments.map((d) => {
        const preview = d.preview || '';
        const shortDesc = preview.length > 40 ? `${preview.substring(0, 40)}...` : preview;
        return {
          id: d.id,
          title: d.title,
          description: shortDesc,
          agentName: d.agentUuid ? (getAgentById(d.agentUuid)?.name || '—') : '',
          driveName: d.driveUuid ? d.vaultName ?? 'Drive' : '',
          driveUuid: d.driveUuid ?? null,
        };
      }),
    [sortedDocuments, getAgentById]
  );

  const handleDocumentSelect = useCallback(
    (doc) => {
      openDocumentPanel({ documentId: doc.id });
    },
    [openDocumentPanel]
  );

  const handleDocumentDeleteClick = useCallback(
    (docOrId) => {
      const doc =
        typeof docOrId === 'object'
          ? docOrId
          : getDocument(docOrId) ?? { id: docOrId, title: '' };
      if (doc?.id) setDocumentToDelete(doc);
    },
    [getDocument]
  );

  const handleDocumentSearchMenuChange = useCallback(
    (value, doc) => {
      if (SAVE_TO_DRIVE_VALUES.includes(value)) {
        setSaveToDriveDocId(doc?.id ?? null);
        setShowSaveToDriveModal(true);
      } else if (GO_TO_DRIVE_VALUES.includes(value) && doc?.driveUuid) {
        navigate(`/v2/drives/d/${doc.driveUuid}`);
      } else if (DOWNLOAD_PDF_VALUES.includes(value) || DOWNLOAD_DOCX_VALUES.includes(value)) {
        const fullDoc = doc?.id ? getDocument(doc.id) : null;
        if (!fullDoc) {
          toast.error({ message: 'Document not found.' });
          return;
        }
        const content = fullDoc.content ?? '';
        const isEmpty = !content?.trim() || content === 'No content available';
        if (isEmpty) {
          toast.error({ message: 'Document is empty.' });
          return;
        }
        const rawTitle = fullDoc.title ?? 'Document';
        const title = rawTitle.replace(/\s+/g, '_').trim() || 'Document';
        const stateToUse = { toText: () => content };
        const promise = DOWNLOAD_PDF_VALUES.includes(value)
          ? generatePdf(stateToUse, title)
          : generateDocx(stateToUse, title);
        promise.catch((err) => {
          toast.error({
            message: err?.message ?? `Failed to generate ${DOWNLOAD_PDF_VALUES.includes(value) ? 'PDF' : 'DOCX'}.`,
          });
        });
      } else if (ADD_FROM_DRIVE_VALUES.includes(value)) {
        onAddFromDriveRequest?.();
      }
    },
    [navigate, getDocument, toast, onAddFromDriveRequest]
  );

  const handleAddMenuChange = useCallback(
    (value, itemData) => {
      if (ADD_FROM_DRIVE_VALUES.includes(value)) {
        onAddFromDriveRequest?.();
      }
      return value;
    },
    [onAddFromDriveRequest]
  );

  // SaveToDrive modal handlers
  const handleCloseSaveToDriveModal = useCallback(() => {
    setShowSaveToDriveModal(false);
    setSaveToDriveDocId(null);
  }, []);

  const handleSaveToDrive = useCallback(
    async (fileName, fileType, destinations) => {
      const result = await saveToVault(fileName, fileType, destinations);
      if (result && saveToDriveDocId && updateDocument) {
        updateDocument(saveToDriveDocId, {
          driveUuid: result.vaultUuid,
          vaultName: result.vaultName,
          title: result.fileName ?? fileName,
        });
      }
      setShowSaveToDriveModal(false);
      setSaveToDriveDocId(null);
    },
    [saveToVault, saveToDriveDocId, updateDocument]
  );

  // Delete modal handlers
  const handleCloseDeleteModal = useCallback(() => setDocumentToDelete(null), []);

  const handleConfirmDeleteDocument = useCallback(async () => {
    const id = documentToDelete?.id;
    if (!id) {
      setDocumentToDelete(null);
      return;
    }
    try {
      const fullDoc = getDocument(id) ?? documentToDelete;
      await handleDeleteDocument(fullDoc);
    } catch {
      // handleDeleteDocument already shows toast on error
    } finally {
      setDocumentToDelete(null);
    }
  }, [documentToDelete, getDocument, handleDeleteDocument]);

  const commonDocProps = useMemo(
    () => ({
      documents: mappedDocuments,
      onDocumentSelect: handleDocumentSelect,
      onDocumentDelete: handleDocumentDeleteClick,
      onDocumentSearchMenuChange: handleDocumentSearchMenuChange,
    }),
    [mappedDocuments, handleDocumentSelect, handleDocumentDeleteClick, handleDocumentSearchMenuChange]
  );

  return {
    // Ready-to-spread props for ConversationView / AgentView / EmptyStateView
    commonDocProps,

    // SaveToDrive modal
    showSaveToDriveModal,
    docToSave,
    isSaving,
    handleCloseSaveToDriveModal,
    handleSaveToDrive,

    // Delete document modal
    documentToDelete,
    handleCloseDeleteModal,
    handleConfirmDeleteDocument,
  };
};
